import type { SessionBalance } from 'shared';

/**
 * Outstanding balances by lease, from `/api/me`.
 *
 * Shared by the staff dashboards and both portals, which is the whole point: the
 * endpoint returns whatever the caller's policies permit, so an accountant sees the
 * delinquency list and a tenant sees their own balance from the identical request.
 * There is no owner-flavoured or tenant-flavoured version of this to keep in sync.
 *
 * Money is a decimal string end to end and is rendered verbatim (invariant 2).
 * Nothing here parses it, sums it, or formats it as a number — `Number('1250.10')`
 * back to a string is how a rent roll starts disagreeing with the ledger by a cent.
 * A total across leases belongs in a SQL SUM on a Decimal column, not in this file.
 */
export function BalanceTable({
  balances,
  emptyMessage = 'Nothing outstanding.',
  caption,
  showPendingPayment = false,
}: {
  balances: SessionBalance[];
  emptyMessage?: string;
  caption?: string;
  /**
   * Replace "Xd late"/"current" outright with a "payment pending" pill for a
   * row whose lease has a PENDING/PROCESSING payment in flight — the softer
   * framing the tenant-facing pages opt into. Off by default.
   *
   * Independent of that: a secondary "$X processing" pill always appears
   * alongside the age pill when a pending amount exists, regardless of this
   * flag. Staff's rent-roll/delinquency views (`RentRoll.tsx`,
   * `Delinquency.tsx`, `StaffDashboard.tsx`) need the true collection status
   * visible at all times — an ACH debit can still return — so a payment in
   * flight is surfaced as an addition to "Xd late", never a replacement of
   * it, for staff. Only the opt-in tenant pages get the full swap.
   */
  showPendingPayment?: boolean;
}) {
  if (balances.length === 0) {
    return <p className="muted">{emptyMessage}</p>;
  }

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>Property</th>
            <th>Unit</th>
            <th className="num">Balance</th>
            <th className="num">Past due</th>
            <th>Age</th>
          </tr>
        </thead>
        <tbody>
          {balances.map((balance) => {
            const pending = Number(balance.pendingPaymentAmount ?? '0');
            return (
              <tr key={balance.leaseId}>
                <td>{balance.propertyName}</td>
                <td>{balance.unitLabel}</td>
                <td className="num">${balance.totalBalance}</td>
                <td className="num">${balance.pastDueBalance}</td>
                <td>
                  {showPendingPayment && pending > 0 ? (
                    <span className="pill">payment pending</span>
                  ) : (
                    <>
                      {balance.maxDaysLate > 0 ? (
                        <span className="pill pill--late">{balance.maxDaysLate}d late</span>
                      ) : (
                        <span className="pill">current</span>
                      )}
                      {pending > 0 ? (
                        <span className="pill pill--info" style={{ marginLeft: '0.35rem' }}>
                          ${balance.pendingPaymentAmount} processing
                        </span>
                      ) : null}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {caption ? (
        <p className="muted" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
          {caption}
        </p>
      ) : null}
    </>
  );
}
